/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { NonGenerativeAIAnswerResultShownProps } from '../../../../ai-answer-dialog/types';
import type { SainAction } from '../../../../common/controller/analytics/hooks/use-sain-usage';
import type { IssueEdge } from '../../api/useIssueSearchQuery/types';

import { IssueKeyNode } from './issue-table-components/IssueKeyNode';
import { IssuePriorityNode } from './issue-table-components/IssuePriorityNode';
import { IssueStatusNode } from './issue-table-components/IssueStatusNode';
import { IssueTextNode } from './issue-table-components/IssueTextNode';
import { IssueTypeNode } from './issue-table-components/IssueTypeNode';
import { IssueUserNode } from './issue-table-components/IssueUserNode';
import { messages } from './messages';

const styles = cssMap({
	tableWrapper: {
		overflowX: 'auto',
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
	},
	table: {
		tableLayout: 'fixed',
		borderStyle: 'hidden',
	},
	header: {
		borderStyle: 'none',
	},
	headerCell: {
		font: token('font.heading.xxsmall'),
		color: token('color.text.subtle'),
		backgroundColor: token('elevation.surface.sunken'),
		paddingTop: token('space.0'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.100'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		height: '40px',
		verticalAlign: 'middle',
	},
	cell: {
		paddingTop: token('space.0'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.100'),
		width: '100%',
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		height: '40px',
		overflow: 'hidden',
	},
});

const HeaderRow = ({ headerLabels }: { headerLabels: { label: string; width: string }[] }) => {
	return headerLabels.map((item, index) => {
		return (
			<th
				key={`issue.header.${index}`}
				css={styles.headerCell}
				style={{
					width: item.width,
					// Override to avoid global first child padding
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					...(index === 0 && { paddingLeft: token('space.100') }),
				}}
			>
				{item.label}
			</th>
		);
	});
};

const BodyRows = ({
	issues,
	analyticsAttributes,
	fireEngagementEvent,
}: {
	issues: IssueEdge[];
	analyticsAttributes: NonGenerativeAIAnswerResultShownProps;
	fireEngagementEvent: (sainAction: SainAction) => void;
}) =>
	issues.map((issue, rowIndex) => {
		const issueNode = issue.node;
		const issueTypeField = issueNode.issueTypeField;
		const issueStatus = issueNode.status;
		const issueAssignee = issueNode.assigneeField.user;
		const issuePriority = issueNode.priorityField;

		return (
			<tr key={`issue.row.${rowIndex}`}>
				<td
					css={styles.cell}
					// Override to avoid global first child padding
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={{ paddingLeft: token('space.100') }}
				>
					<Inline alignBlock="center" alignInline="start" space="space.075">
						<IssueTypeNode IssueTypeField={issueTypeField} />
						<IssueKeyNode
							url={issueNode.webUrl}
							issuekey={issueNode.key}
							analyticsAttributes={analyticsAttributes}
							fireEngagementEvent={fireEngagementEvent}
						/>
						<IssueTextNode text={issueNode.summary} />
					</Inline>
				</td>
				<td css={styles.cell}>
					<IssueStatusNode status={issueStatus} />
				</td>
				<td css={styles.cell}>
					<IssueUserNode user={issueAssignee} />
				</td>
				<td css={styles.cell}>
					<IssuePriorityNode IssuePriorityField={issuePriority} />
				</td>
			</tr>
		);
	});

export const IssueTable: ({
	issues,
	analyticsAttributes,
	fireEngagementEvent,
}: {
	issues: IssueEdge[];
	analyticsAttributes: NonGenerativeAIAnswerResultShownProps;
	fireEngagementEvent: (sainAction: SainAction) => void;
}) => JSX.Element = ({
	issues,
	analyticsAttributes,
	fireEngagementEvent,
}: {
	issues: IssueEdge[];
	analyticsAttributes: NonGenerativeAIAnswerResultShownProps;
	fireEngagementEvent: (sainAction: SainAction) => void;
}) => {
	const { formatMessage } = useIntl();
	const headerLabels = [
		{ label: formatMessage(messages.headerWork), width: '60%' },
		{ label: formatMessage(messages.headerStatus), width: '15%' },
		{ label: formatMessage(messages.headerAssignee), width: '15%' },
		{ label: formatMessage(messages.headerPriority), width: '10%' },
	];

	return (
		<Box xcss={styles.tableWrapper}>
			<table css={styles.table} data-testid="issue-table">
				<thead css={styles.header}>
					<tr>
						<HeaderRow headerLabels={headerLabels} />
					</tr>
				</thead>
				<tbody>
					{issues.length > 0 ? (
						<BodyRows
							issues={issues}
							analyticsAttributes={analyticsAttributes}
							fireEngagementEvent={fireEngagementEvent}
						/>
					) : (
						<tr>
							<td align="center" colSpan={headerLabels.length} css={styles.cell}>
								<Inline alignBlock="center" alignInline="center">
									{formatMessage(messages.noResultsMessage)}
								</Inline>
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</Box>
	);
};
